import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function ClaimAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const email = (searchParams.get("email") || "").toLowerCase();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      toast.error("Lien d'activation invalide ou incomplet");
    }
  }, [token, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Mot de passe : 6 caractères minimum"); return; }
    if (pwd !== pwd2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/claim", { token, email, new_password: pwd });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      toast.success("Bienvenue sur VroumZy !");
      // Hard refresh so AuthContext picks up the new tokens via /auth/me
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'activation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md rounded-3xl border border-zinc-200 shadow-sm">
        <CardHeader className="pb-2 text-center">
          <div className="mx-auto mb-2 flex items-center justify-center"><BrandLogo size="md" /></div>
          <div className="flex items-center justify-center gap-2 text-emerald-700">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-[0.15em]">Bienvenue !</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-zinc-950 mt-2">Activez votre compte</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Choisissez un mot de passe pour finaliser votre inscription
          </p>
        </CardHeader>
        <CardContent>
          {token && email ? (
            <form onSubmit={submit} className="space-y-4" data-testid="claim-form">
              <div>
                <Label className="text-xs text-zinc-500">Email</Label>
                <Input value={email} disabled className="rounded-xl bg-zinc-50" data-testid="claim-email" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Nouveau mot de passe</Label>
                <Input type="password" autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Au moins 6 caractères" className="rounded-xl" data-testid="claim-password" required minLength={6} />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Confirmer le mot de passe</Label>
                <Input type="password" autoComplete="new-password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="rounded-xl" data-testid="claim-password-confirm" required minLength={6} />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white h-11" data-testid="claim-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Activer mon compte
              </Button>
              <p className="text-[11px] text-zinc-500 text-center">
                Vous serez automatiquement connecté(e) après activation.
              </p>
            </form>
          ) : (
            <div className="text-center space-y-3 py-4">
              <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto" />
              <p className="text-sm text-zinc-700">Le lien d&apos;activation est incomplet ou invalide.</p>
              <Button onClick={() => navigate("/register")} variant="outline" className="rounded-xl">
                S&apos;inscrire normalement
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
